/**
 * buildCampaignPayload — wizard form state → API payload.
 *
 * Decisions baked into this implementation:
 *
 *   1. Preheader is rendered as a hidden <div> at the very top of the body
 *      so it shows in the inbox preview snippet but not in the email itself.
 *      This is the email-client convention. The preheader column is also
 *      stored separately on the backend for analytics/future templating.
 *
 *   2. CTA renders as a styled <a> button appended to the body. If cta_text
 *      is empty, no button is added. If cta_url is empty but cta_text exists,
 *      we still render the button with href="#" so the layout is preserved
 *      (the wizard's URL validator catches this earlier in normal flow).
 *
 *   3. `name` stays separate from `subject`. The backend has its own column
 *      and the campaigns list shows it as the primary label. Subject is what
 *      recipients see in their inbox.
 *
 *   4. Merge tags ({{first_name}}, {{company_name}}) are replaced before send
 *      because the current backend has no personalization engine. Without
 *      replacement, recipients would literally see raw {{tokens}}. Unknown
 *      tokens are stripped entirely as a safety net.
 *
 *   5. Schedule mapping:
 *        - 'now'       → status:'sent',      no scheduled_at
 *        - 'scheduled' → status:'scheduled', scheduled_at = ISO of local date+time
 *        - 'draft'     → status:'draft',     no scheduled_at
 */

const TOKEN_KNOWN = [
  [/{{\s*first_name\s*}}/gi, 'there'],
  [/{{\s*company_name\s*}}/gi, null], // filled in below from ctx.senderName
];
const TOKEN_UNKNOWN = /{{[^}]+}}/g;

function replaceTokens(input, senderName) {
  if (!input) return '';
  let out = String(input);
  for (const [re, repl] of TOKEN_KNOWN) {
    out = out.replace(re, repl ?? senderName ?? '');
  }
  return out.replace(TOKEN_UNKNOWN, '');
}

function escapeHtml(s) {
  return String(s).replace(/[<>&"']/g, (c) => (
    { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

export function buildCampaignPayload(form, ctx) {
  const statusMap = { now: 'sent', scheduled: 'scheduled', draft: 'draft' };
  const status = statusMap[form.schedule] || 'sent';

  let scheduled_at;
  if (status === 'scheduled' && form.schedDate && form.schedTime) {
    // Treat the wizard's date+time as local; toISOString() converts to UTC,
    // which is what Laravel's `date` validator + Carbon parser expect.
    scheduled_at = new Date(`${form.schedDate}T${form.schedTime}`).toISOString();
  }

  // Compose the body: preheader (hidden) + body + optional CTA button.
  let body = replaceTokens(form.body, ctx.senderName);

  const preheaderText = (form.preheader || '').trim();
  if (preheaderText) {
    const safe = escapeHtml(replaceTokens(preheaderText, ctx.senderName));
    body = `<div style="display:none;max-height:0;overflow:hidden;font-size:1px;line-height:1px;color:#ffffff;opacity:0">${safe}</div>${body}`;
  }

  const ctaText = (form.cta_text || '').trim();
  const ctaUrl = (form.cta_url || '').trim();
  if (ctaText) {
    const safeText = escapeHtml(ctaText);
    const safeUrl = ctaUrl ? escapeHtml(ctaUrl) : '#';
    body += `<p style="margin:24px 0"><a href="${safeUrl}" style="display:inline-block;background:#0891b2;color:#ffffff;padding:10px 22px;border-radius:8px;text-decoration:none;font-weight:600">${safeText}</a></p>`;
  }

  // Build the payload. Keys with null/undefined are omitted so the backend's
  // `nullable` validators don't have to special-case empty strings.
  const payload = {
    status,
    recipients: Array.isArray(ctx.recipients) ? ctx.recipients : [],
  };

  const name = (form.name || '').trim();
  if (name) payload.name = name;

  const subject = replaceTokens((form.subject || '').trim(), ctx.senderName);
  if (subject) payload.subject = subject;

  if (preheaderText) payload.preheader = preheaderText;

  if (body) payload.body = body;

  if (ctaText) payload.cta_text = ctaText;
  if (ctaUrl) payload.cta_url = ctaUrl;

  if (scheduled_at) payload.scheduled_at = scheduled_at;

  return payload;
}
